import { AbscenceType } from "domain/entities/abscence-type";
import { PurchaseOrder } from "domain/entities/purchase-order";
import { TimesheetDay } from "domain/entities/timesheet-day";
import { EMAIL } from "domain/enum/email";
import { AbscenceTypeInputFilter } from "domain/models/abscence-type-input-filter";
import { PurchaseOrderInputFilter } from "domain/models/purchase-order-input-filter";
import DropdownComponent from "presentation/components/Shared/DropdownComponent";
import InputNumberComponent from "presentation/components/Shared/InputNumberComponent";
import { useAbscenceTypesByFilter } from "presentation/hooks/use-query-abscence-type";
import { usePurchaseOrdersByFilter } from "presentation/hooks/use-query-purchase-order";
import { useUpdateTimesheetMutation } from "presentation/hooks/use-query-timesheet";
import {
  useCreateTimesheetDayMutation,
  useDeleteTimesheetDayMutation,
  useUpdateTimesheetDayMutation,
} from "presentation/hooks/use-query-timesheet-day";
import { useTimesheetFormStore } from "presentation/stores/Timesheet/use-timesheet-form-store";
import { useAuthStore } from "presentation/stores/use-auth-store";
import { EventType } from "presentation/stores/use-timesheet-calendar-store";
import { Button } from "primereact/button";
import { Checkbox } from "primereact/checkbox";
import { RadioButton } from "primereact/radiobutton";
import { Toast } from "primereact/toast";
import { useEffect, useMemo, useRef, useState } from "react";
import { Parameter } from "domain/entities/parameter";
import { abscenceTypesInDays } from "../TimesheetCalendar";

/**
 * Propriedades para o componente `TimesheetDayForm`.
 */
interface ParamProps {
  /** A data selecionada para o registo. */
  currentDate: Date | null;
  /** O evento existente a ser editado. Se `undefined`, o formulário está em modo de criação. */
  currentEvent?: EventType;
  /** Lista de todos os eventos do calendário para validações. */
  calendarEvents?: EventType[];
  /** Flag que indica se o formulário deve estar desabilitado. */
  isDisable: boolean;
  /** Callback para notificar o componente pai sobre o estado de processamento (ex: spinner). */
  setIsProcessing: (isProcessing: boolean) => void;
  /** Callback para atualizar os eventos no calendário pai. */
  setCalendarEvents: (events: EventType[]) => void;
  /** Callback executado após uma operação de salvar bem-sucedida. */
  onSave(options: {
    applyToAll: boolean;
    timesheetDayForm: TimesheetDayForm | null;
  }): void;
  /** Callback executado após uma operação de apagar bem-sucedida. */
  onDelete(): void;
}

interface TimesheetDayForm {
  id?: string;
  timesheetDayId?: string | undefined;
  typeRecord?: string;
  purchaseOrder?: Pick<PurchaseOrder, "id" | "name" | "type">;
  purchaseOrderHours?: number;
  abscence?: Pick<AbscenceType, "id" | "name" | "documentRequired">;
  abscenceHours?: number;
  document?: Document;
}

/**
 * Componente de formulário para criar ou editar um registo diário de Timesheet.
 *
 * Este formulário gere a lógica de adicionar/editar/remover um registo de projeto ou ausência
 * para um dia específico, validando as horas e comunicando o resultado ao calendário pai.
 * @param {ParamProps} params - As propriedades do componente.
 */
const TimesheetDayForm: React.FC<ParamProps> = (params: ParamProps) => {
  const toast = useRef<Toast>(null);

  const [checked, setChecked] = useState(false);
  const serviceTypes = ["Serviço", "Deslocação", "Prevenção", "Hora Extra"];

  const [isHourDisable, setIsHourDisable] = useState(false);
  const [currentAbscenceType, setCurrentAbscenceType] =
    useState<AbscenceType>();

  // Objeto para associar form aos dados do timesheetDay
  const [timesheetDayForm, setTimesheetDayForm] =
    useState<TimesheetDayForm | null>(null);

  const { userEmployee, email: userEmployeeEmail } = useAuthStore();

  // Mantém o estado da timeseet, timesheetDay e events
  const {
    currentTimesheet,
    currentTimesheetDay,
    // currentEvents,
    setCurrentTimesheet,
    setCurrentTimesheetDay,
  } = useTimesheetFormStore();
  // const { timesheetDay, setTimesheetDay } = useTimesheetDayStore();
  const deleteTimesheetDay = useDeleteTimesheetDayMutation(); // Retorna uma Promise
  const updateTimesheetDay = useUpdateTimesheetDayMutation(); // Retorna uma Promise
  const createTimesheetDay = useCreateTimesheetDayMutation(); // Retorna uma Promise
  const { mutate: updateTimesheet } = useUpdateTimesheetMutation();

  // Obtém lista de POs
  const { data: purchaseOrderData } = usePurchaseOrdersByFilter({
    limit: 100,
    sortBy: "name",
    sortOrder: "asc",
    employeeId: userEmployee?.id,
  } as PurchaseOrderInputFilter);

  // Obtém lista de tipos de ausência filtrados por contractType do employee
  const { data: abscencesData } = useAbscenceTypesByFilter({
    limit: 100,
    sortBy: "name",
    sortOrder: "asc",
    contractType: userEmployee?.contractType,
  } as AbscenceTypeInputFilter);

  const [isAbscenceInDays, setIsAbscenceInDays] = useState(false);
  const isSaveDisabled =
    isAbscenceInDays &&
    timesheetDayForm?.typeRecord === "abscence" &&
    timesheetDayForm?.abscenceHours !== 1;

  useEffect(() => {
    // Define se o tipo de ausência selecionado é medido em dias (ex: Férias)
    // em vez de horas.
    if (currentAbscenceType?.name) {
      setIsAbscenceInDays(
        abscenceTypesInDays.includes(currentAbscenceType.name)
      );
    }
  }, [currentAbscenceType]);

  /**
   * Manipulador para a mudança de horas de ausência.
   * Valida se o tipo de ausência permite um valor diferente de 1 dia (8h).
   * @param newValue O novo valor de horas/dias.
   */
  const handleAbscenceHoursChange = (newValue: number) => {
    if (isAbscenceInDays) {
      if (newValue !== 1 && newValue !== null && newValue !== undefined) {
        toast.current?.show({
          severity: "error",
          summary: "Valor inválido",
          detail: "Este tipo de ausência só permite 1 dia (8 horas).",
          life: 3000,
        });
        return;
      }
    }
    setTimesheetDayForm({
      ...timesheetDayForm,
      abscenceHours: newValue,
    });
  };

  /**
   * Formata os dados de Purchase Orders para serem usados no DropdownComponent.
   * `useMemo` é usado para evitar recálculos desnecessários a cada renderização.
   */
  const purchaseOrders = useMemo<Parameter[]>(() => {
    if (!purchaseOrderData?.data) return [];
    return purchaseOrderData.data.map((item) => ({
      id: item.id,
      value: item.name,
      type: "purchaseOrder",
    }));
  }, [purchaseOrderData]);

  /**
   * Formata os dados de Tipos de Ausência para serem usados no DropdownComponent.
   */
  const abscences = useMemo<Parameter[]>(() => {
    if (!abscencesData?.data) return [];
    return abscencesData.data.map((item) => ({
      id: item.id,
      value: item.name,
      type: "abscenceType",
      disabled: item.name === "Férias" || item.name === "Feriado"
    }));
  }, [abscencesData]);

  useEffect(() => {
    // Efeito para reagir à seleção de um tipo de ausência no dropdown.
    if (
      timesheetDayForm?.abscence?.id &&
      timesheetDayForm.typeRecord === "abscence"
    ) {
      const abscenceType = abscencesData?.data?.find(
        (item) => item.id === timesheetDayForm.abscence?.id
      );

      const isInDays = abscenceTypesInDays.includes(abscenceType?.name || "");

      if (isInDays) {
        setIsHourDisable(true);
        timesheetDayForm.abscenceHours = 1;
      } else {
        setIsHourDisable(false);
      }

      setTimesheetDayForm((prev) => ({
        ...prev,
        abscence: {
          ...prev?.abscence,
          id: abscenceType?.id,
          name: abscenceType?.name || "",
          documentRequired: abscenceType?.documentRequired || false,
        },
        abscenceHours: isInDays ? 1 : prev?.abscenceHours,
      }));

      setCurrentAbscenceType(abscenceType);
    }
  }, [timesheetDayForm?.abscence?.id]);

  useEffect(() => {
    // Efeito para reagir à seleção de um projeto (PO) no dropdown.
    if (timesheetDayForm?.purchaseOrder?.id) {
      const purchaseOrder = purchaseOrderData?.data?.find(
        (item) => item.id === timesheetDayForm.purchaseOrder?.id
      );
      timesheetDayForm.purchaseOrder.name = purchaseOrder?.name || "";
      timesheetDayForm.purchaseOrder.type = purchaseOrder?.type || "Serviço";
      setIsHourDisable(false);
      if (
        timesheetDayForm.purchaseOrder.name.includes("Deslocação") ||
        timesheetDayForm.purchaseOrder.name.includes("Prevenção")
      ) {
        timesheetDayForm.purchaseOrderHours = 0;
        setIsHourDisable(true);
      }
      setTimesheetDayForm((prev) => ({
        ...prev,
        purchaseOrder: {
          ...prev?.purchaseOrder,
          id: purchaseOrder?.id,
          name: purchaseOrder?.name || "",
          type: purchaseOrder?.type || "Serviço",
        },
        purchaseOrderHours: isHourDisable ? 0 : prev?.purchaseOrderHours,
      }));
    }
  }, [timesheetDayForm?.purchaseOrder?.id]);

  useEffect(() => {
    // Efeito para inicializar o formulário quando um evento existente é selecionado para edição.
    if (params.currentEvent) {
      if (params.currentEvent.type !== "abscence") {
        setTimesheetDayForm({
          typeRecord: params.currentEvent.type,
          id: params.currentEvent.id,
          purchaseOrder: {
            id: params.currentEvent?.eventId,
            name: "",
            type: "project",
          },
          purchaseOrderHours: params.currentEvent.hours,
          abscence: undefined,
          abscenceHours: undefined,
        });
      } else {
        setTimesheetDayForm({
          typeRecord: params.currentEvent.type,
          id: params.currentEvent.id,
          abscence: {
            id: params.currentEvent?.eventId,
            name: "",
            documentRequired: currentAbscenceType?.documentRequired || false,
          },
          abscenceHours: params.currentEvent.hours,
          purchaseOrder: undefined,
          purchaseOrderHours: undefined,
        });
      }
    }
  }, [params.currentEvent]);

  /**
   * Apaga o registo atual.
   * Se for o último registo do dia, apaga o `TimesheetDay`.
   * Caso contrário, apenas remove o registo específico e atualiza o `TimesheetDay`.
   */
  const deleteEvent = async () => {
    // Não permitir exclusão de eventos de férias
    if (params.currentEvent?.title?.toLowerCase() === "férias") {
      toast.current?.show({
        severity: "warn",
        summary: "Ação não permitida",
        detail: "Não é possível excluir eventos de férias.",
        life: 3000,
      });
      return;
    }

    params.setIsProcessing(true);
    try {
      // Remove ou atualiza o evento no BD
      if (currentTimesheetDay && currentTimesheetDay.id) {
        currentTimesheetDay.lastModifiedBy = userEmployeeEmail ?? EMAIL;
        const totalEvents =
          (currentTimesheetDay.purchaseOrders?.length || 0) +
          (currentTimesheetDay.abscenceType ? 1 : 0);

        if (totalEvents > 1) {
          // Se tem mais de 1 evento, remove da lista e atualiza o BD
          if (timesheetDayForm?.typeRecord === "project" || timesheetDayForm?.typeRecord === "extraHour") {
            if (currentTimesheetDay.purchaseOrders) {
              currentTimesheetDay.purchaseOrders =
                currentTimesheetDay.purchaseOrders.filter(
                  (item) => item.id !== timesheetDayForm?.purchaseOrder?.id
                );
            }
            if (
              currentTimesheetDay.abscenceTime &&
              currentTimesheetDay.abscenceType
            ) {
              currentTimesheetDay.abscenceType.hours =
                currentTimesheetDay.abscenceTime;
            }
          } else if (timesheetDayForm?.typeRecord === "abscence") {
            currentTimesheetDay.abscenceTime = 0;
            currentTimesheetDay.abscenceType = undefined;
          }

          setCurrentTimesheetDay({ ...currentTimesheetDay });
          await updateTimesheetDay(currentTimesheetDay);
        } else if (currentTimesheetDay.id) {
          // Se tem apenas 1 evento, apaga o registo do dia
          await deleteTimesheetDay(currentTimesheetDay);
        }
      }
      params.onDelete();
    } catch (error) {
      toast.current?.show({
        severity: "error",
        summary: "Erro ao Apagar",
        detail: "Ocorreu um erro ao apagar o registo.",
        life: 3000,
      });
    } finally {
      // O spinner será desativado pelo useEffect principal do calendário após o refetch
      // que é chamado dentro do params.onDelete()
    }
  };

  /**
   * Salva o registo atual (cria um novo ou atualiza um existente).
   */
  const saveEvent = () => {
    if (!params.currentDate) return;

    if (
      currentTimesheetDay &&
      abscenceTypesInDays.includes(currentTimesheetDay.abscenceType?.name || "")
    ) {
      toast.current?.show({
        severity: "error",
        summary: "Ausência diária já registada",
        detail: `Já existe um evento de ausência diária registado para este dia.`,
        life: 1500,
      });
      return;
    }

    if (
      currentTimesheetDay &&
      abscenceTypesInDays.includes(timesheetDayForm?.abscence?.name || "") &&
      currentTimesheetDay.purchaseOrders &&
      currentTimesheetDay.purchaseOrders?.length > 0
    ) {
      toast.current?.show({
        severity: "error",
        summary: "Ausência diária já registada",
        detail: `Já existe um evento de projeto registado para este dia.`,
        life: 1500,
      });
      return;
    }

    if (timesheetDayForm && abscenceTypesInDays.includes(timesheetDayForm?.abscence?.name || "")) {
      timesheetDayForm.abscenceHours = userEmployee?.workHoursPerDay;
    }

    let sumHours =
      timesheetDayForm?.typeRecord === "abscence"
        ? timesheetDayForm.abscenceHours ?? 0
        : timesheetDayForm?.purchaseOrderHours ?? 0;

    sumHours += currentTimesheetDay?.abscenceTime ?? 0;
    sumHours +=
      currentTimesheetDay?.purchaseOrders?.reduce(
        (acc, item) => acc + (item.hours || 0),
        0
      ) ?? 0;

    if (sumHours > 14) {
      toast.current?.show({
        severity: "error",
        summary: "Limite excedido",
        detail: `Não é permitido adicionar mais de 14 horas no mesmo dia.`,
        life: 1500,
      });
      return;
    }

    if (
      timesheetDayForm?.typeRecord === "abscence" &&
      currentTimesheetDay?.abscenceType
    ) {
      // Já existe um evento de ausência, não permite adicionar outro
      toast.current?.show({
        severity: "error",
        summary: "Ausência já registada",
        detail: `Já existe um evento de ausência registado para este dia.`,
        life: 1500,
      });
      return;
    }

    const isTherePO = currentTimesheetDay?.purchaseOrders?.find(
      (item) => item.id === timesheetDayForm?.purchaseOrder?.id
    );
    if (timesheetDayForm?.typeRecord === "abscence" && isTherePO) {
      // Já existe um evento de projeto, não permite adicionar outro
      toast.current?.show({
        severity: "error",
        summary: "Projeto já registado",
        detail: `Já existe um evento de projeto registado para este dia.`,
        life: 1500,
      });
      return;
    }
    let eventId =
      timesheetDayForm?.typeRecord === "project" || timesheetDayForm?.typeRecord === "extraHour"
        ? timesheetDayForm?.purchaseOrder?.id
        : timesheetDayForm?.abscence?.id;

    let hours =
      timesheetDayForm?.typeRecord === "project" || timesheetDayForm?.typeRecord === "extraHour"
        ? timesheetDayForm?.purchaseOrderHours
        : timesheetDayForm?.abscenceHours;

    let title =
      timesheetDayForm?.typeRecord === "project" || timesheetDayForm?.typeRecord === "extraHour"
        ? timesheetDayForm?.purchaseOrder?.name
        : timesheetDayForm?.abscence?.name;

    let start = new Date(params.currentDate);
    start.setHours(9);

    let end = new Date(start);
    end.setHours(start.getHours() + (hours ?? 0));
    const newEvent = {
      id: "",
      eventId: eventId || "",
      title: title || "",
      type: timesheetDayForm?.typeRecord || "project",
      hours: hours || 0,
      start: start,
      end: end,
    };

    // Verifica se existem eventos e se não
    if (params.calendarEvents) {
      const sameDayEvents = params.calendarEvents.filter(
        (ev) =>
          ev.start.getFullYear() === newEvent.start.getFullYear() &&
          ev.start.getMonth() === newEvent.start.getMonth() &&
          ev.start.getDate() === newEvent.start.getDate()
      );

      const totalHours =
        sameDayEvents.reduce((sum, ev) => sum + ev.hours, 0) + newEvent.hours;

      if (totalHours > 14) {
        toast.current?.show({
          severity: "error",
          summary: "Limite excedido",
          detail: `Não é permitido adicionar mais de 14 horas no mesmo dia.`,
          life: 1500,
        });
        return;
      }
    }

    if (currentTimesheet) {
      let startAt = new Date(
        currentTimesheet?.year,
        currentTimesheet?.month - 1,
        params.currentDate.getDate(),
        0,
        0,
        0
      );
      let endAt = new Date(
        currentTimesheet?.year,
        currentTimesheet?.month - 1,
        params.currentDate.getDate(),
        23,
        59,
        59
      );
      // console.log("calendarEvents", params.calendarEvents);
      const records = params.calendarEvents?.filter(
        (item) => item.start >= startAt && item.start <= endAt
      );

      // console.log("records", records);

      let timesheetDayObject: TimesheetDay = {
        day: params.currentDate.getDate(),
        timesheet: {
          id: currentTimesheet.id,
        },
        lastModifiedBy: userEmployeeEmail ?? EMAIL,
      };

      if (timesheetDayForm?.typeRecord === "abscence") {
        timesheetDayObject.abscenceType = {
          id: timesheetDayForm?.abscence?.id,
          hours: timesheetDayForm?.abscenceHours || 0,
          name: timesheetDayForm?.abscence?.name || "",
        };
        timesheetDayObject.abscenceTime = timesheetDayForm?.abscenceHours || 0;
      } else {
        timesheetDayObject.purchaseOrders = [
          {
            id: timesheetDayForm?.purchaseOrder?.id,
            name: timesheetDayForm?.purchaseOrder?.name || "",
            hours: timesheetDayForm?.purchaseOrderHours || 0,
          },
        ];
      }

      if (records && records.length > 0) {
        records.forEach((item) => {
          timesheetDayObject.id ??= item.id;
          if (item.type === "project" || item.type === "extraHour") {
            timesheetDayObject.purchaseOrders?.push({
              id: item.eventId,
              name: item.title,
              hours: item.hours,
            });
          } else if (item.type === "abscence") {
            // Adiciona a verificação hasAbscence
            timesheetDayObject.abscenceType = {
              id: item.eventId,
              hours: item.hours,
              name: item.title,
            };
          }
        });
      }
      if (timesheetDayObject) {
        // console.log("object", timesheetDayObject);

        const mutationOptions = {
          onSuccess: () => {
            if (currentTimesheet && currentTimesheet.status === "Novo") {
              const updatedTimesheet = {
                ...currentTimesheet,
                status: "Editado",
                lastModifiedBy: userEmployeeEmail ?? EMAIL,
              };
              setCurrentTimesheet(updatedTimesheet);
              updateTimesheet(updatedTimesheet);
            }
            params.onSave({
              applyToAll: checked,
              timesheetDayForm,
            });
          },
          onError: (error: any) => {
            const message =
              error?.response?.data?.metadata?.errors?.[0] ||
              "Ocorreu um erro inesperado";
            toast.current?.show({
              severity: "error",
              summary: "Erro",
              detail: message,
              life: 3000,
            });
            params.setIsProcessing(false); // Desativa o spinner em caso de erro
          },
        };

        if (timesheetDayObject.id) {
          updateTimesheetDay(timesheetDayObject)
            .then(mutationOptions.onSuccess)
            .catch(mutationOptions.onError);
        } else {
          createTimesheetDay({ ...timesheetDayObject, id: undefined })
            .then(mutationOptions.onSuccess)
            .catch(mutationOptions.onError);
        }
      }
    }
  };

  return (
    <div>
      <div className="default-grid header-spacing">
        <div className="default-2-cols">
          <RadioButton
            disabled={params.isDisable}
            inputId="typeRecord"
            name="project"
            value="project"
            onChange={(e) =>
              setTimesheetDayForm({ ...timesheetDayForm, typeRecord: e.value })
            }
            checked={
              timesheetDayForm?.typeRecord === "project" ||
              timesheetDayForm?.typeRecord === "extraHour" ||
              timesheetDayForm?.typeRecord === undefined
            }
          />
          <label htmlFor="typeRecord" className="ml-2">
            Alocação em Projeto
          </label>
        </div>
        <div className="default-2-cols">
          <RadioButton
            disabled={params.isDisable}
            inputId="typeRecord"
            name="abscence"
            value="abscence"
            onChange={(e) =>
              setTimesheetDayForm({ ...timesheetDayForm, typeRecord: e.value })
            }
            checked={timesheetDayForm?.typeRecord === "abscence"}
          />
          <label htmlFor="typeRecord" className="ml-2">
            Justificação de Ausência
          </label>
        </div>
        {timesheetDayForm?.typeRecord !== "abscence" && (
          <>
            <div className="default-3-cols">
              <DropdownComponent
                options={purchaseOrders ?? []}
                label="Projeto/P.O."
                isValueId={true}
                isDisable={params.isDisable}
                fieldName="purchaseOrder"
                currentObject={timesheetDayForm}
                getValue={timesheetDayForm?.purchaseOrder?.id}
                setValue={setTimesheetDayForm}
              />
            </div>

            <div className="default-1-col">
              <InputNumberComponent
                isDisable={isHourDisable || params.isDisable}
                label="Horas"
                fieldName="purchaseOrderHours"
                currentObject={timesheetDayForm}
                getValue={timesheetDayForm?.purchaseOrderHours}
                setValue={setTimesheetDayForm}
              />
            </div>
          </>
        )}
        {timesheetDayForm?.typeRecord === "abscence" && (
          <>
            <div className="default-3-cols">
              <DropdownComponent
                options={abscences ?? []}
                label="Tipo de ausência"
                isValueId={true}
                isDisable={params.isDisable}
                fieldName="abscence"
                currentObject={timesheetDayForm}
                getValue={timesheetDayForm?.abscence?.id}
                setValue={setTimesheetDayForm}
              />
            </div>

            <div className="default-1-col">
              <InputNumberComponent
                label={isAbscenceInDays ? "Dias" : "Horas"}
                fieldName="abscenceHours"
                isDisable={
                  isHourDisable || params.isDisable || isAbscenceInDays
                }
                currentObject={timesheetDayForm}
                getValue={timesheetDayForm?.abscenceHours}
                setValue={(obj) => handleAbscenceHoursChange(obj.abscenceHours)}
              />
            </div>
          </>
        )}{" "}
        {!params.isDisable &&
          params.currentEvent &&
          params.currentEvent.eventId && (
            <div className="default-4-cols next-bottom custom-toolbar">
              <Button
                label="Excluir"
                onClick={deleteEvent}
                className="button-size"
              />
            </div>
          )}
        {!params.isDisable && !params?.currentEvent?.eventId && (
          <>
            <div className="default-3-cols">
              <div className="flex align-items-center">
                <Checkbox
                  inputId="applyToAll"
                  onChange={(e) => setChecked(e.checked ?? false)}
                  checked={checked}
                />
                <label htmlFor="applyToAll" className="ml-2">
                  Aplicar para todos os dias
                </label>
              </div>
            </div>
            <div className="default-1-col next-bottom custom-toolbar">
              <Button
                label="Guardar"
                onClick={saveEvent}
                className="button-size"
                disabled={isSaveDisabled}
              />
            </div>
          </>
        )}
      </div>

      <Toast ref={toast} position="bottom-right" />
    </div>
  );
};

export default TimesheetDayForm;
